import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from './Header/header';
import SubscribeSection from './Subscribe';
import FooterSection from './Footer';
import { getPostsByCategory } from '../services/postService';

const SITE_URL = 'https://exploroff.com';

export default function StaticPostLayout({ title, category, slug, coverImage, excerpt, tags, readTime, children, mapComponent, whereToStay, publishedDate, modifiedDate }) {
  const [related, setRelated] = useState([]);

  useEffect(() => {
    getPostsByCategory('hiking-guides')
      .then(posts => setRelated(posts.filter(p => p.id !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug]);

  const categoryLabel = category === 'hiking-guides' ? 'Hiking Guides' : 'Itineraries';
  const categoryPath  = category === 'hiking-guides' ? '/hiking-guides' : '/itineraries';

  const canonical = `${SITE_URL}${typeof window !== 'undefined' ? window.location.pathname : '/'}`;
  const metaTitle  = `${title} | ExplorOFF`;
  const metaDesc   = excerpt || `${title} — curated outdoor guide from ExplorOFF.`;
  const ogImage    = coverImage
    ? (coverImage.startsWith('http') ? coverImage : `${SITE_URL}${coverImage}`)
    : `${SITE_URL}/img/logo-color-1x.png`;

  const isHowTo = /^how\s+to\b/i.test(title);
  const howToSchema = isHowTo ? {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: title,
    description: metaDesc,
    url: canonical,
    ...(readTime && { totalTime: `PT${parseInt(readTime, 10)}M` }),
  } : null;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: metaDesc,
    image: ogImage,
    url: canonical,
    ...(publishedDate && { datePublished: publishedDate }),
    ...(modifiedDate  && { dateModified: modifiedDate }),
    ...(!modifiedDate && publishedDate && { dateModified: publishedDate }),
    ...(tags && tags.length > 0 && { keywords: tags.join(', ') }),
    publisher: {
      '@type': 'Organization',
      name: 'ExplorOFF',
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/img/logo-color-1x.png` },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',         item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: categoryLabel,  item: `${SITE_URL}${categoryPath}` },
      { '@type': 'ListItem', position: 3, name: title,          item: canonical },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="ExplorOFF" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {howToSchema && (
          <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        )}
      </Helmet>

      <Header bgColor="teal" />

      {mapComponent ? (
        // Itinerary: text header only — map renders full-width below
        <div className="xpost-no-hero">
          <div className="container">
            <nav className="xpost-breadcrumb xpost-breadcrumb-dark" aria-label="breadcrumb">
              <a href="/">Home</a>
              <i className="ti-angle-right" />
              <a href={categoryPath}>{categoryLabel}</a>
              <i className="ti-angle-right" />
              <span>{title}</span>
            </nav>
            <h1 className="xpost-title-plain">{title}</h1>
            {excerpt && <p className="xpost-excerpt-plain">{excerpt}</p>}
          </div>
        </div>
      ) : coverImage ? (
        <div className="xpost-hero">
          <img
            src={coverImage}
            alt={title}
            className="xpost-hero-img"
            loading="eager"
          />
          <div className="xpost-hero-overlay">
            <div className="container">
              <nav className="xpost-breadcrumb" aria-label="breadcrumb">
                <a href="/">Home</a>
                <i className="ti-angle-right" />
                <a href={categoryPath}>{categoryLabel}</a>
                <i className="ti-angle-right" />
                <span>{title}</span>
              </nav>
              <h1 className="xpost-title">{title}</h1>
              {excerpt && <p className="xpost-excerpt">{excerpt}</p>}
              {readTime && (
                <div className="xpost-meta">
                  <span><i className="ti-timer" style={{ marginRight: '4px' }} />{readTime} min read</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="xpost-no-hero">
          <div className="container">
            <nav className="xpost-breadcrumb xpost-breadcrumb-dark" aria-label="breadcrumb">
              <a href="/">Home</a>
              <i className="ti-angle-right" />
              <a href={categoryPath}>{categoryLabel}</a>
              <i className="ti-angle-right" />
              <span>{title}</span>
            </nav>
            <h1 className="xpost-title-plain">{title}</h1>
            {excerpt && <p className="xpost-excerpt-plain">{excerpt}</p>}
          </div>
        </div>
      )}

      {/* Google Map route — full-width, before content */}
      {mapComponent}

      <div className="xpost-body">
        <div className="container">
          <div className="xpost-grid">

            <article className="xpost-content xpost-content-static">
              {children}
            </article>

            <aside className="xpost-sidebar">
              <div className="xpost-sidebar-card xpost-sidebar-cta-card">
                <div className="xpost-sidebar-cta-icon">
                  <i className="ti-map-alt" />
                </div>
                <h4>Share Your Route</h4>
                <p>Been Somewhere Epic? Submit your itinerary and inspire the ExplorOFF community.</p>
                <a href="/submit-itinerary" className="xpost-sidebar-cta-btn">
                  Submit Your Itinerary
                </a>
              </div>

              {whereToStay && whereToStay.length > 0 && (
                <div className="xpost-sidebar-card xpost-stay-card">
                  <h4 className="xpost-stay-title">Where to Stay</h4>
                  <div className="xpost-stay-list">
                    {whereToStay.map((s, i) => (
                      <a key={i} href={s.url} className="xpost-stay-item" target="_blank" rel="noopener noreferrer">
                        <span className="xpost-stay-name">{s.name}</span>
                        <span className="xpost-stay-type">
                          {s.type.includes('·') ? s.type.split('·').slice(1).join('·').trim() : s.type}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {tags && tags.length > 0 && (
                <div className="xpost-sidebar-card">
                  <h4>Tags</h4>
                  <div className="xpost-tags-list">
                    {tags.map(t => (
                      <span key={t} className="xpost-tag-pill">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="xpost-sidebar-card xpost-browse-card">
                <h4>Explore More</h4>
                <a href="/hiking-guides" className="xpost-browse-link">
                  <i className="ti-map" /> All Hiking Guides
                </a>
                <a href="/itineraries" className="xpost-browse-link">
                  <i className="ti-location-pin" /> All Itineraries
                </a>
              </div>
            </aside>

          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="xrelated-section">
          <div className="container">
            <h3 className="xrelated-title">More Guides You'll Like</h3>
            <div className="xrelated-grid">
              {related.map(r => (
                <a key={r.id} href={`/hiking-guides/${r.id}`} className="xrelated-card">
                  {r.coverImage && (
                    <img
                      src={r.coverImage}
                      alt={r.title}
                      className="xrelated-card-img"
                      loading="lazy"
                    />
                  )}
                  <div className="xrelated-card-body">
                    <h4 className="xrelated-card-title">{r.title}</h4>
                    {r.excerpt && <p className="xrelated-card-excerpt">{r.excerpt}</p>}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      <SubscribeSection />
      <FooterSection />
    </>
  );
}
